import React, { useState, useEffect, useReducer, useContext } from 'react';
import {
    AsGrid,
    AsSearchBar,
    AsTabContainer,
    AsTab,
    useModal,
    mapPageInfo,
    AsButton,
    isAuthorised,
    Spacer,
    getClaimValue
} from '@adserve/adserve-react-components';
import { apiParamsReducer } from '../../services/AsGridService';
import CampaignsGridColumnDefinitions from './GridColumnDefinitions';
import { useHistory, useLocation } from 'react-router-dom';
import {
    GetCampaignInListingFormat,
    GetCampaigns,
    ToggleCampaignFavourite,
    DeleteCampaign,
    CreateCampaign,
    GetCampaign,
    GetReservations,
    GetReservationTimes,
    GetCampaignSettings,
    GetCampaignPropertyStats,
    GetLatestVersionCampaignDiscounts
} from '../../services/CampaignsDataService';
import { gridContainerStyle } from '../../styles';
import { SystemSettingsContext } from '../../contexts/SystemSettingsContext';
import ReservationsGridColumnDefinitions from './ReservationsGridColumnDefinitions';
import RadioDropdownButton from '../generic/adserve-react-components/radio-dropdown-button/RadioDropdownButton';
import { BusinessTypes } from '../../constants/Enums';
import { optionTextStyle } from '../FilterOptionsList/FilterOptionsListStyles';
import FilterOptionsList from '../FilterOptionsList/FilterOptionsList';
import { AirtimePlanningContext } from '../../contexts/AirtimePlanningContext';
import { useDebounce } from './useDebounce';

// Styles
const gridItem = {
    textAlign: 'center',
    padding: 0
};
const tabArea = {
    gridColumn: '1 / 4',
    paddingTop: 18
};
const tableArea = {
    gridColumn: '1 / 4',
    backgroundColor: '#FFFFFF'
};
const whiteButtonStyle = {
    border: '2px solid #CCCCCC',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer'
};

const centre = {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center'
};
const optionText = {
    color: '#414141',
    fontFamily: 'Roboto',
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: 0,
    cursor: 'pointer'
};

const defaultPageInfo = {
    count: 0,
    hasNextPage: false,
    hasPrevPage: false,
    isFirstPage: true,
    isLastPage: true,
    pageCount: 1,
    pageNumber: 1,
    pageSize: -1,
    totalItemCount: 0,
    pageSizeOptions: [{ value: -1, text: 'All' }]
};

const HomePage = () => {
    const { dateFormat, timeFormat, locale, currency } = useContext(SystemSettingsContext);
    const { propertyStats, setPropertyStats, setPropertyTotals, setDisableBackButton, fetchCampaignPropertyStats } =
        useContext(AirtimePlanningContext);
    const history = useHistory();
    const location = useLocation();

    // States
    const [tab, setTab] = useState(location.state?.tabId || 0);
    const [data, setData] = useState([]);
    const [newItem, setNewItem] = useState();
    const [reservationTimeValues, setReservationTimeValues] = useState([]);
    const [scrolling, setScrolling] = useState(false);
    const [reservationWorkflowId, setReservationWorkflowId] = useState();
    const { addMessage } = useModal();
    const [filterOptions, setFilterOptions] = useState(['Airtime', 'SAndP', 'OffAirs', 'Other']);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm);
    const campaignTypes = [
        { value: 'Airtime', text: 'Airtime' },
        { value: 'SAndP', text: 'S&P' },
        { value: 'OffAirs', text: 'OffAirs' },
        { value: 'Other', text: 'Other' }
    ];
    const loadData = () => {
        let dataFound = false;
        if (apiParams.reservations) {
            GetReservations(apiParams)
                .then((data) => {
                    setData(data.data);
                    setPageInfo(mapPageInfo(data));
                    dataFound = true;
                })
                // eslint-disable-next-line no-console
                .catch(console.log);
        } else {
            GetCampaigns(apiParams)
                .then((data) => {
                    for (const row of data.data) {
                        row.totalCost = row.budget + row.offairCost;
                    }
                    setData(data.data);
                    setPageInfo(mapPageInfo(data));
                    dataFound = true;
                })
                // eslint-disable-next-line no-console
                .catch(console.log);
            loadNewItem();
        }
        if (!dataFound) {
            setData([]);
            setPageInfo(defaultPageInfo);
        }
    };

    const getGridColumnDefinitions = (scrolling = false) => {
        if (tab === 4) {
            return ReservationsGridColumnDefinitions(loadData, reservationTimeValues, scrolling, reservationWorkflowId);
        } else {
            return CampaignsGridColumnDefinitions();
        }
    };

    const [columnDefinitions, setColumnDefinitions] = useState(getGridColumnDefinitions());

    const handleScroll = () => {
        if (!scrolling) setScrolling(true);
    };

    const handleFilterOptionUpdate = (val) => {
        const updatedFilterOptions = [...filterOptions];
        const filterIdx = filterOptions?.findIndex((f) => f === val);
        if (filterIdx !== -1) {
            updatedFilterOptions.splice(filterIdx, 1);
        } else {
            updatedFilterOptions.push(val);
        }
        setFilterOptions(updatedFilterOptions);
        apiParamsDispatcher({
            ...apiParams,
            campaignTypes: updatedFilterOptions,
            pageNumber: 1
        });
    };

    useEffect(() => {
        GetReservationTimes().then((data) => {
            setReservationTimeValues(data);
        });

        GetCampaignSettings()
            .then((data) => {
                setReservationWorkflowId(data.reservationWorkflowId);
            })
            .catch(console.log);

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const [pageInfo, setPageInfo] = useState(defaultPageInfo);

    const apiParamDefaults = {
        sortField: 'createdDate',
        sortOrder: 'desc',
        pageNumber: 1,
        pageSize: 15,
        favouritesOnly: false,
        viewedOnly: false,
        reservations: false,
        myCampaigns: false,
        campaignTypes: ['Airtime', 'SAndP', 'OffAirs', 'Other']
    };

    const [apiParams, apiParamsDispatcher] = useReducer(apiParamsReducer, apiParamDefaults);

    // Handlers
    const handleFilterChange = (field) => {
        const columnToUpdate = columnDefinitions.find((c) => c.field === field);
        columnToUpdate.showInList = !columnToUpdate.showInList;

        const unchangedColumns = columnDefinitions.filter((c) => c.field !== columnToUpdate.field);

        setColumnDefinitions([...unchangedColumns, columnToUpdate]);
    };

    const handleSearchUpdate = (value) => {
        setSearchTerm(value);
    };

    const handlePageSizeChange = (option) => {
        apiParamsDispatcher({ pageSize: option.value, pageNumber: 1 });
    };

    const handlePrevClick = () => {
        const newPageNumber = apiParams.pageNumber - 1;
        if (newPageNumber >= 1) {
            apiParamsDispatcher({ ...apiParams, pageNumber: newPageNumber });
        }
    };

    const handleNextClick = () => {
        const newPageNumber = apiParams.pageNumber + 1;
        if (newPageNumber <= pageInfo.pageCount) {
            apiParamsDispatcher({ ...apiParams, pageNumber: newPageNumber });
        }
    };

    const handleSortOrderChange = (column) => {
        const unchangedColumns = columnDefinitions
            .filter((c) => c.field !== column.field)
            .map((c) => ({ ...c, sortOrder: '' }));

        const sortField = column.field === 'status' ? 'statusEnumOrder' : column.field;

        setColumnDefinitions([...unchangedColumns, column]);
        apiParamsDispatcher({
            sortField: sortField,
            sortOrder: column.sortOrder
        });
    };

    const copyCampaign = async (campaign) => {
        const allProperties = [];
        if (campaign?.campaignTypeName?.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase()) {
            fetchCampaignPropertyStats(campaign?.id)
                .then(async (result) => {
                    for (const property of result?.propertiesStats) {
                        if (typeof property?.weeks === 'undefined' && property?.weekNumber) {
                            property.weeks = property?.weekNumber;
                        }
                        var weekNo;
                        const startDate = new Date(property?.startDate);
                        const endDate = new Date(property?.endDate);
                        const diffInMs = new Date(endDate) - new Date(startDate);
                        weekNo = Math.floor(diffInMs / (1000 * 60 * 60 * 24 * 7)) + 1;
                        property.defaultSandPBudget = property?.sandPBudget / weekNo;
                        property.defaultImpacts = property?.totalImpacts / weekNo;
                        property.defaultPropertyListPrice = property?.propertyListPrice / weekNo;
                        property.sandPBudget = property.sandPBudget?.toFixed(2);
                        allProperties.push(property);
                        // }
                    }
                    setPropertyStats(allProperties);
                    setPropertyTotals(result);

                    await GetCampaign(campaign.id)
                        .then((c) => {
                            delete c.id;
                            delete c.algorithmId;
                            delete c.versionNumber;
                            delete c.campaignStageId;
                            delete c.forecastCategoryId;
                            delete c.probability;
                            delete c.closeDate;

                            const adserveId = getClaimValue('ASId');
                            c.plannerId = adserveId;
                            c.salesExecId = adserveId;
                            c.jetExecId = adserveId;
                            c.campaignOwnerId = adserveId;

                            c.name = `${c.name} (copy)`;
                            c.spotTypes = [];
                            c.scriptFactors = [];
                            c.copyTypes = [];
                            c.campaignContacts = [];
                            c.jcn = null;

                            // Get the latest version of the deal and use that - all of this should be backend but I'm not going to change it now
                            GetLatestVersionCampaignDiscounts(campaign.id).then((newCampaignDiscounts) => {
                                c.dealVersionId = newCampaignDiscounts.dealVersionId;
                                c.campaignDiscounts = newCampaignDiscounts.campaignDiscounts;

                                CreateCampaign(c, allProperties)
                                    .then((entity) => {
                                        history.push({
                                            pathname: '/campaigns/details',
                                            state: {
                                                campaignId: entity.id,
                                                readOnly: false
                                            }
                                        });
                                    })
                                    // eslint-disable-next-line no-console
                                    .catch(console.log);
                            });
                        })
                        // eslint-disable-next-line no-console
                        .catch(console.log);
                })
                .catch((error) => {
                    console.log(error);
                });
        } else {
            await GetCampaign(campaign.id).then((c) => {
                delete c.id;
                delete c.algorithmId;
                delete c.versionNumber;
                delete c.campaignStageId;
                delete c.forecastCategoryId;
                delete c.probability;
                delete c.closeDate;

                const adserveId = getClaimValue('ASId');
                c.plannerId = adserveId;
                c.salesExecId = adserveId;
                c.jetExecId = adserveId;
                c.campaignOwnerId = adserveId;

                c.name = `${c.name} (copy)`;
                c.spotTypes = [];
                c.scriptFactors = [];
                c.copyTypes = [];
                c.campaignContacts = [];
                c.jcn = null;

                // Get the latest version of the deal and use that - all of this should be backend but I'm not going to change it now
                GetLatestVersionCampaignDiscounts(campaign.id).then((newCampaignDiscounts) => {
                    c.dealVersionId = newCampaignDiscounts.dealVersionId;
                    c.campaignDiscounts = newCampaignDiscounts.campaignDiscounts;

                    CreateCampaign(c, allProperties)
                        .then((entity) => {
                            history.push({
                                pathname: '/campaigns/details',
                                state: {
                                    campaignId: entity.id,
                                    readOnly: false
                                }
                            });
                        })
                        // eslint-disable-next-line no-console
                        .catch(console.log);
                });
            });
        }
    };

    const deleteCampaign = async (campaign) => {
        await DeleteCampaign(campaign.id)
            .then(() => {
                loadData();
            })
            // eslint-disable-next-line no-console
            .catch(console.log);
    };

    const handleActionClick = (command, data) => {
        switch (command) {
            case 'edit':
                history.push({
                    pathname: '/campaigns/details',
                    state: { campaignId: data.id, readOnly: false, tabId: tab, isEditClick: true }
                });
                break;
            case 'copy':
                addMessage({
                    message: window.translate(`Are you sure you want to copy ${data.name}?`),
                    buttons: [
                        {
                            text: window.translate('No'),
                            style: { ...whiteButtonStyle, width: 87 },
                            callback: null
                        },
                        {
                            text: window.translate('Yes'),
                            style: { cursor: 'pointer' },
                            callback: () => copyCampaign(data)
                        }
                    ]
                });
                break;
            case 'delete':
                addMessage({
                    message: window.translate(`Are you sure you want to delete ${data.name}?`),
                    buttons: [
                        {
                            text: window.translate('No'),
                            style: { ...whiteButtonStyle, width: 87 },
                            callback: null
                        },
                        {
                            text: window.translate('Yes'),
                            style: { cursor: 'pointer' },
                            callback: () => deleteCampaign(data)
                        }
                    ]
                });
                break;
            default:
                break;
        }
    };

    const handleIconDisabilty = (actionType, status, campaign) => {
        const disableDelete = ['Draft', 'Reserved', '', 'Booked'];
        const deleteActionIcons = ['delete'];

        const disableEdit = ['Qualified Out'];
        const editActionIcons = ['edit'];

        return (
            (deleteActionIcons.includes(actionType) &&
                (!disableDelete.includes(status) || campaign?.isRunningCampaign || !campaign?.isMyCampaign)) ||
            (editActionIcons.includes(actionType) && disableEdit.includes(status))
        );
    };

    const handleToolTip = (actionType, campaign) => {
        if (actionType === 'delete') {
            if (campaign?.isRunningCampaign) {
                return {
                    toolTipText: window.translate('Cannot delete a running campaign'),
                    width: 210
                };
            } else if (!campaign?.isMyCampaign) {
                return {
                    toolTipText: window.translate('This record can only be deleted by the person who created it'),
                    width: 354
                };
            }
        }
        return null;
    };

    // handleFavouriteClick event returns a row data. Destruct to find any id you like to use.
    const handleFavouriteClick = ({ id }) => {
        ToggleCampaignFavourite(id)
            .then(() => {
                loadData();
            })
            // eslint-disable-next-line no-console
            .catch(console.log);
    };

    const handlePrimaryColumnClick = (entity) => {
        history.push({
            pathname: '/campaigns/details',
            state: {
                campaignId: apiParams.reservations ? entity.campaignId : entity.id,
                readOnly: true,
                tabId: tab
            }
        });
    };

    const handleCreateNewButtonClick = (businessType) => {
        if (businessType === BusinessTypes.DIGITAL.name) {
            //     return;
        }
        // if (businessType == BusinessTypes.AIRTIMEBURST.name) {
        //     addMessage({
        //         modalStyle: { width: 1400, height: 900 },
        //         message: <AirtimeBurstDialog></AirtimeBurstDialog>
        //     });
        // } else {
        history.push({
            pathname: '/campaigns/create',
            state: {
                campaignId: window.emptyGuid,
                readOnly: false,
                businessType
            }
        });
        // }
    };

    const loadNewItem = () => {
        if (location?.state?.redirectedFrom !== 'create') {
            return;
        }

        GetCampaignInListingFormat(location.state.id)
            .then((data) => {
                setNewItem(data);
            })
            // eslint-disable-next-line no-console
            .catch(console.log);
    };

    const handleTabClick = (tabIndex) => {
        switch (tabIndex) {
            case 1:
                apiParamsDispatcher({
                    ...apiParamDefaults,
                    sortField: 'lastViewed',
                    sortOrder: 'desc',
                    myCampaigns: true,
                    campaignTypes: [...filterOptions]
                });
                break;

            case 2:
                apiParamsDispatcher({
                    ...apiParamDefaults,
                    sortField: 'lastViewed',
                    sortOrder: 'desc',
                    viewedOnly: true,
                    myCampaigns: false,
                    campaignTypes: [...filterOptions]
                });
                break;

            case 3:
                apiParamsDispatcher({
                    ...apiParamDefaults,
                    favouritesOnly: true,
                    myCampaigns: false,
                    campaignTypes: [...filterOptions]
                });
                break;

            case 4:
                apiParamsDispatcher({
                    ...apiParamDefaults,
                    reservations: true,
                    myCampaigns: false,
                    campaignTypes: [...filterOptions]
                });
                break;

            default:
                apiParamsDispatcher({
                    ...apiParamDefaults,
                    myCampaigns: false,
                    campaignTypes: [...filterOptions]
                });
                break;
        }
        setTab(tabIndex);
    };

    useEffect(() => {
        apiParamsDispatcher({ ...apiParams, searchTerm: debouncedSearchTerm });
    }, [debouncedSearchTerm]);

    useEffect(() => {
        setDisableBackButton(false);
        loadData();
    }, [apiParams]);
    const [language] = useState(window.language);

    useEffect(() => {
        setColumnDefinitions(getGridColumnDefinitions());
    }, [language, tab, window.translationStrings]);
    const [dropDownOptions, setDropDownOptions] = useState([]);
    const reportOptions = [
        {
            id: 'livecontent',
            name: 'Live Content Day Report'
        }
    ];
    useEffect(() => {
        const dt = Object.keys(BusinessTypes).map((a, b) => {
            return {
                id: BusinessTypes[a].name,
                name: window.translate(BusinessTypes[a].text),
                canCreate: BusinessTypes[a].canCreate
            };
            //                return window.translate(BusinessTypes[a].text);
        });
        setDropDownOptions(dt.filter((a) => a.canCreate));

        if (tab === 4 && data.length > 0) {
            const myInterval = setInterval(
                () => {
                    setColumnDefinitions(getGridColumnDefinitions(scrolling));
                    if (scrolling) setScrolling(false);
                },
                scrolling ? 0 : 1000
            );
            return () => {
                clearInterval(myInterval);
            };
        }
    }, []);

    const handleJetImportClick = () => {
        history.push({
            pathname: '/jet/import'
        });
    };

    const handleViewReportClick = (report) => {
        history.push({
            pathname: `/reports/${report}`
        });
    };

    return (
        <div style={{ ...gridContainerStyle, boxSizing: 'border-box', width: '100vw' }}>
            <div style={gridItem} />

            <div style={{ ...gridItem }}>
                <AsSearchBar
                    id='campaignSearch'
                    placeholder={window.translate(
                        apiParams.reservations ? 'Search Reservations...' : 'Search Campaigns...'
                    )}
                    width={395}
                    height={42}
                    currentSearchTerm={searchTerm}
                    onSearchUpdate={handleSearchUpdate}
                />
            </div>

            <div
                style={{
                    ...gridItem,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center'
                }}
            >
                <AsButton
                    style={{ width: 130, fontSize: 14, fontWeight: 700, marginRight: '5%' }}
                    handleClick={handleJetImportClick}
                    disabled={!isAuthorised('IMPORT_JET')}
                >
                    {window.translate('Import from J-ET')}
                </AsButton>
                <RadioDropdownButton
                    id='new-campaign'
                    text={window.translate('Create New')}
                    options={dropDownOptions}
                    createClick={handleCreateNewButtonClick}
                />
                <Spacer width='5%' height={0} />
                <RadioDropdownButton
                    id='view-report'
                    text={window.translate('Reports')}
                    options={reportOptions}
                    createClick={handleViewReportClick}
                    actionText={window.translate('View')}
                />
            </div>
            <div
                style={{
                    ...gridItem,
                    ...centre,
                    ...optionText,
                    gridColumn: '3 / 4',
                    zIndex: 100,
                    width: '73%',
                    marginLeft: 168
                }}
            >
                <FilterOptionsList
                    options={campaignTypes}
                    filterOptions={filterOptions}
                    optionChange={handleFilterOptionUpdate}
                />
            </div>
            <div style={{ ...gridItem, ...tabArea, height: 39, marginTop: -50 }}>
                <AsTabContainer tabPanelStyle={{ height: 0 }} onTabClick={handleTabClick} initalTab={tab}>
                    <AsTab title={window.translate('All Campaigns')} />
                    <AsTab title={window.translate('My Campaigns')} />
                    <AsTab title={window.translate('Recently Viewed')} />
                    <AsTab title={window.translate('Favourites')} />
                    <AsTab title={window.translate('Reservations')} />
                </AsTabContainer>
            </div>
            <div language={language} style={{ ...gridItem, ...tableArea }}>
                <AsGrid
                    columnDefs={columnDefinitions}
                    pageInfo={pageInfo}
                    scrollableAreaStyle={{ height: 'calc(100% - 12px)' }}
                    data={data}
                    newlyCreated={newItem}
                    primaryColumn='name'
                    onPageSizeChange={tab === 2 ? null : handlePageSizeChange}
                    onNextClick={tab === 2 ? null : handleNextClick}
                    onPrevClick={tab === 2 ? null : handlePrevClick}
                    onSortOrderChange={tab === 2 ? null : handleSortOrderChange}
                    onActionClick={handleActionClick}
                    onFavouriteClick={handleFavouriteClick}
                    onPrimaryColumnClick={handlePrimaryColumnClick}
                    onColumnFilterChange={handleFilterChange}
                    onActionIconLoad={handleIconDisabilty ?? false}
                    onActionToolTip={handleToolTip}
                    showColumnFilter={true}
                    onScroll={handleScroll}
                    dateFormat={dateFormat}
                    timeFormat={timeFormat}
                    locale={locale}
                    currency={currency}
                    actionIconSize={19}
                    emptyRowHeight={34}
                    rowHeight={34}
                    headerRowHeight={34}
                />
            </div>
        </div>
    );
};

export default HomePage;
